// Packages Import
import { memo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Code4ReportAction } from "../../../store/Code4Report/Code4Report-slice";
import moment from "moment";

// Project Components
import { GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector } from "@mui/x-data-grid";
import InfoTable from "../../UI/Table";
import { Button, IconButton, InputAdornment, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ColoredTextField from "../../UI/ColoredTextField";
import AlternativeNoteCell from "./Component/AlternativeNoteCell";
import ControlledMessageBox from "../../Util/ControlledMessagebox";
import ErrorBox from "../../Util/ErrorBox";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { LoadingButton } from "@mui/lab";

const actionSelectOption = ["Replace", "BB", "LTB"];

const Code4PartTable = (props) => {
	const dispatch = useDispatch();
	const nonMtlEngAccess = useSelector((state) => state.authenticationState.Non_Mlt_Eng_Access);

	const columnVisibility = useSelector((state) => state.code4ReportState.tableColumnVisibility);
	const code4PartsTable = useSelector((state) => state.code4ReportState.code4PartsTable);
	const editedTable = useSelector((state) => state.code4ReportState.editedTable);
	const excelIsLoading = useSelector((state) => state.code4ReportState.excelIsLoading);
	const filteredData = useSelector((state) => state.code4ReportState.filteredData);

	const code4PartsEditStates = useSelector((state) => state.code4ReportState.code4PartsEditStates);
	const editing = code4PartsEditStates.editing;
	const borderSetting = code4PartsEditStates.borderSetting;
	const buttonText = code4PartsEditStates.buttonText;

	const prevFilteredData = useSelector((state) => state.code4ReportState.prevFilteredData);

	const userInfo = {
		username: useSelector((state) => state.authenticationState.userNTID),
		org: useSelector((state) => state.authenticationState.User_Assigned_Orgs),
		name: useSelector((state) => state.authenticationState.userName),
	};

	const isInOrg = userInfo.org.allOrg.includes(prevFilteredData.userOrg);

	const [controlledMessage, setControlledMessage] = useState("");
	const [openControlMessageBox, setOpencontrolMessageBox] = useState(false);

	const [errorMessage, setErrorMsg] = useState("");
	const [errorState, setErrorState] = useState(false);

	// Inside columnsSettings:
	const columnsSettings = [
		{ field: "kpn", headerName: "KPN", type: "text", minWidth: 50, flex: 0.3 },
		{ field: "pref_code", headerName: "Pref Code", type: "text", minWidth: 50, flex: 0.2 },
		{ field: "bom_no", headerName: "BOM", type: "text", minWidth: 50, flex: 0.3 },
		{
			field: "action",
			headerName: "Action",
			type: "select",
			selectOptions: actionSelectOption,
			editable: true,
			minWidth: 50,
			flex: 0.2,
		},
		{
			field: "action_date",
			headerName: "Date MM/YYYY",
			type: "date",
			minWidth: 50,
			flex: 0.4,
			renderCell: (params) => {
				if (editing) {
					return (
						<LocalizationProvider dateAdapter={AdapterMoment}>
							<DatePicker
								disabled={!params.row.action}
								value={params.value || null}
								onChange={(value) => {
									const updatedRow = {
										...params.row,
										action_date: value ? moment(value).format("yyyy-MM") + "-02" : "", // Manually append a day to make saving in database possible
									};
									dispatch(Code4ReportAction.updateEditedTable(updatedRow));
								}}
								views={["year", "month"]}
								inputFormat="MM/YYYY"
								renderInput={(inputParams) => (
									<ColoredTextField
										{...inputParams}
										variant="standard"
										autoComplete="off"
										sx={{ width: "100%" }}
										InputProps={{
											startAdornment: ["Replace", "BB"].includes(params.row.action) ? (
												<InputAdornment position="start">
													<span style={{ color: "red", fontWeight: 700 }}>*</span>
												</InputAdornment>
											) : (
												<span style={{ paddingRight: 15 }}></span>
											),
											endAdornment: (
												<>
													{params.value ? (
														<InputAdornment position="end">
															<IconButton
																size="small"
																onClick={() => {
																	const updatedRow = { ...params.row, action_date: "" };
																	dispatch(Code4ReportAction.updateEditedTable(updatedRow));
																}}
															>
																<ClearIcon fontSize="small" />
															</IconButton>
														</InputAdornment>
													) : null}
													{inputParams.InputProps?.endAdornment}
												</>
											),
										}}
										onKeyDown={(event) => event.preventDefault()}
									/>
								)}
							/>
						</LocalizationProvider>
					);
				}
				// Check if the value is a valid date and cast it to YYYY/MM
				return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])/.test(params.value) ? moment(params.value).format("MM/YYYY") : "";
			},
		},
		{
			field: "alternative_note",
			headerName: "alternative KPN, MPN, NOTE",
			editable: false,
			minWidth: 50,
			flex: 0.6,

			renderCell: (params) => {
				if (editing) {
					return <AlternativeNoteCell row={params.row} isRequired={["Replace", "BB"].includes(params.row.action)} disabled={!params.row.action} />;
				}
				return params.value || "";
			},
		},
		{ field: "note", headerName: "KPN Note", type: "text", minWidth: 50, flex: 0.6 },
	];

	const handleSelectChange = (params) => (event) => {
		console.log(params.field);

		const updatedRow = { ...params.row, [params.field]: event.target.value };
		dispatch(Code4ReportAction.updateEditedTable(updatedRow));
	};

	const handleEditMode = () => {
		props.unblockRef.current();
		if (editing) {
			const tableHasEdited = JSON.stringify(editedTable) !== JSON.stringify(code4PartsTable);
			if (tableHasEdited) {
				const mandatoryCheckList = editedTable.filter((val) => val.action === "Replace" || val.action === "BB");
				let showError = false;
				mandatoryCheckList.forEach((val) => {
					if (!val.action_date || !val.alternative_note?.trim()) {
						showError = true;
					}
				});
				console.log(mandatoryCheckList);
				if (showError) {
					setErrorState(true);
					setErrorMsg("Please fill in all mandatory fields for action with 'Replace' or 'BB'");
				} else {
					const requestJSON = {
						user_org: userInfo.org.primaryOrg,
						code4_details: editedTable.map((val) => ({
							kpn: val.kpn,
							pref_code: val.pref_code,
							bom_no: val.bom_no,
							action: val.action,
							action_date: val.action_date || null,
							alternative_note: val.alternative_note || "",
						})),
					};
					console.log(requestJSON);

					props.updateCode4Parts(requestJSON);
				}
			} else {
				console.log("No Changes");
				setOpencontrolMessageBox(true);
				setControlledMessage("No changes detected");
				dispatch(Code4ReportAction.clearEditedTable());
				dispatch(Code4ReportAction.setEditingTable(false));
				setTimeout(() => {
					setOpencontrolMessageBox(false);
					setControlledMessage("");
				}, 1000);
			}
		} else {
			dispatch(Code4ReportAction.setEditingTable(true));
		}
	};

	const handleCancelEdit = () => {
		props.unblockRef.current();
		dispatch(Code4ReportAction.clearEditedTable());
		// dispatch(Code4ReportAction.retrievePart(fullData));
		dispatch(Code4ReportAction.setEditingTable(false));
	};

	const ExtraProps = {
		sx: {
			...borderSetting,
			"& .MuiDataGrid-columnHeaderTitle": {
				textOverflow: "clip",
				whiteSpace: "break-spaces",
				lineHeight: 1.3,
			},
			height: 533,
		},
		headerHeight: 105,
		columnVisibilityModel: columnVisibility,
		onColumnVisibilityModelChange: (newModel) => {
			dispatch(Code4ReportAction.setColumnVisibility({ columnVisibility: newModel }));
		},
	};

	const customToolBar = () => (
		<>
			<GridToolbarColumnsButton />
			<GridToolbarFilterButton />
			<GridToolbarDensitySelector />
			<Tooltip title={editing ? "Export is not available when editing" : ""} placement="top" onClick={props.handleExport}>
				<span>
					<LoadingButton
						loading={excelIsLoading}
						loadingPosition="start"
						startIcon={<FileDownloadOutlinedIcon />}
						disabled={editing || code4PartsTable.length === 0}
					>
						Export
					</LoadingButton>
				</span>
			</Tooltip>
			{nonMtlEngAccess || !isInOrg || !filteredData.from_email ? (
				""
			) : (
				<>
					<Button
						disabled={code4PartsTable.length === 0}
						startIcon={editing ? <SaveIcon /> : <EditIcon />}
						sx={{ minWidth: 0 }}
						onClick={handleEditMode}
					>
						{buttonText}
					</Button>
					{editing && (
						<Button startIcon={<CloseIcon />} onClick={handleCancelEdit}>
							Cancel
						</Button>
					)}
				</>
			)}
		</>
	);
	return (
		<>
			<ControlledMessageBox open={openControlMessageBox} message={controlledMessage} />
			<ErrorBox msg={errorMessage} setErrorState={setErrorState} ErrorState={errorState} />

			<InfoTable
				columnsSettings={columnsSettings}
				rows={editing ? editedTable : code4PartsTable}
				maxtableHeight={10}
				height={500}
				autoTableHeight={false}
				rowsPerPageOptions={[10, 50, 100]}
				pageSize={10}
				selectionType="row"
				checkboxSelection={false}
				customToolBar={customToolBar}
				ExtraProps={ExtraProps}
				editing={editing}
				handleSelectChange={handleSelectChange}
			/>
		</>
	);
};

export default memo(Code4PartTable);
